import mixpanel from 'mixpanel-browser';

export const DEFAULT_MIXPANEL_PROXY_URL = 'https://mixpanel.cadactive.com';
export const MIXPANEL_SOURCE = 'web';

const CADACTIVE_CLOUD_HOST = 'cadactive.com';
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

const usableEnvString = (value) => {
  if (value == null) {
    return '';
  }
  const normalized = String(value).trim();
  if (!normalized || normalized === 'undefined' || normalized === 'null') {
    return '';
  }
  return normalized;
};

const readWindowEnv = (key) => {
  if (typeof window === 'undefined' || !window.__ENV__) {
    return '';
  }
  return usableEnvString(window.__ENV__[key]);
};

const stripTrailingSlash = (url) => usableEnvString(url).replace(/\/+$/, '');

export const getEnvNodeEnv = () => {
  const NODE_ENV = String(import.meta.env.NODE_ENV || import.meta.env.MODE || 'development');
  return NODE_ENV;
};

export const getEnvMixpanelToken = () => {
  const MIXPANEL_TOKEN = String(import.meta.env.VITE_MIXPANEL_TOKEN || '');
  return MIXPANEL_TOKEN;
};

export const getEnvMixpanelEnvironment = () => {
  const MIXPANEL_ENVIRONMENT = String(import.meta.env.VITE_MIXPANEL_ENVIRONMENT || 'development');
  return MIXPANEL_ENVIRONMENT;
};

export const getEnvMixpanelProxyUrl = () => {
  const runtime = readWindowEnv('MIXPANEL_PROXY_URL');
  const fromEnv = usableEnvString(
    import.meta.env.VITE_MIXPANEL_PROXY_URL || import.meta.env.MIXPANEL_PROXY_URL
  );
  return stripTrailingSlash(runtime || fromEnv || DEFAULT_MIXPANEL_PROXY_URL) || DEFAULT_MIXPANEL_PROXY_URL;
};

export const getEnvAppVersion = () => {
  const APP_VERSION = String(import.meta.env.VITE_VERSION || 'unknown');
  return APP_VERSION;
};

/**
 * Mixpanel `deployment`: cloud (CadActive-hosted) vs on_premise (KOTS).
 *
 * 1. Explicit CADACTIVE_DEPLOYMENT / VITE_CADACTIVE_DEPLOYMENT (KOTS sets on_premise)
 * 2. Hostname contains cadactive.com, or is localhost → cloud
 * 3. Any other browser host (customer hostname) → on_premise
 */
export const getMixpanelDeployment = () => {
  const explicit = usableEnvString(
    readWindowEnv('CADACTIVE_DEPLOYMENT') ||
    import.meta.env.VITE_CADACTIVE_DEPLOYMENT ||
    import.meta.env.CADACTIVE_DEPLOYMENT
  );
  if (explicit === 'cloud' || explicit === 'on_premise') {
    return explicit;
  }

  const hostname = typeof window !== 'undefined' && window.location
    ? String(window.location.hostname || '').toLowerCase()
    : '';
  if (!hostname || LOCAL_HOSTS.has(hostname) || hostname.includes(CADACTIVE_CLOUD_HOST)) {
    return 'cloud';
  }
  return 'on_premise';
};

// Ring buffer for events that arrive before initMixpanel() has run.
// `initMixpanel` is now deferred to requestIdleCallback so there is a
// short window (usually < 300 ms) during which `track()` callers would
// otherwise be dropped. We cap the buffer so a misbehaving caller can't
// grow it unbounded.
const MIXPANEL_PENDING_CAP = 100;
let mixpanelIsInitialised = false;
const pendingMixpanelEvents = [];

const flushPendingMixpanelEvents = () => {
  if (!pendingMixpanelEvents.length) {
    return;
  }
  // Drain synchronously so order is preserved.
  const drained = pendingMixpanelEvents.splice(0, pendingMixpanelEvents.length);
  drained.forEach(({ eventName, properties }) => {
    try {
      mixpanel.track(eventName, properties);
    } catch (e) {
      console.error('Failed to flush buffered Mixpanel event:', e);
    }
  });
};

export const initMixpanel = () => {

  if (getEnvNodeEnv() === 'development') {
    console.log('--------------------------------');
    console.log('Initializing Mixpanel');
    console.log('- NODE_ENV:', getEnvNodeEnv());
    console.log('- Token:', getEnvMixpanelToken());
    console.log('- Environment:', getEnvMixpanelEnvironment());
    console.log('- Proxy URL:', getEnvMixpanelProxyUrl());
    console.log('- Deployment:', getMixpanelDeployment());
    console.log('- App Version:', getEnvAppVersion());
    console.log('--------------------------------');
  }

  if (!isMixpanelConfigured()) {
    console.warn('Mixpanel is disabled - Token not found');
    return false;
  }

  try {
    // Initialize with optimal configuration
    mixpanel.init(getEnvMixpanelToken(), {
      api_host: getEnvMixpanelProxyUrl(),
      // debug: NODE_ENV === 'development',
      // track_pageview: false, // We handle this manually for more control
      // persistence: 'localStorage',
      // ignore_dnt: false, // Respect Do Not Track
      // batch_requests: true, // Batch multiple events
      // secure_cookie: true,
      // property_blacklist: ['$current_url', '$initial_referrer', '$referrer'], // We track these manually
      // disable_persistence: true, // Disable automatic persistence to prevent device ID generation
      // disable_cookie: true, // Disable cookies to prevent device ID generation
      // cross_subdomain_cookie: false // Prevent cross-subdomain tracking
    });

    // Set global properties that will be sent with every event
    mixpanel.register({
      environment: getEnvMixpanelEnvironment(),
      source: MIXPANEL_SOURCE,
      deployment: getMixpanelDeployment(),
      host: window.location.hostname,
      app_version: getEnvAppVersion(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      user_agent: navigator.userAgent,
      screen_height: window.screen.height,
      screen_width: window.screen.width,
      language: navigator.language,
      language_code: navigator.language.split('-')[0],
      language_region: navigator.language.split('-')[1],
      screen_color_depth: window.screen.colorDepth,
      screen_pixel_depth: window.screen.pixelDepth,
      screen_orientation: window.screen.orientation,
      screen_pixel_ratio: window.devicePixelRatio,
    });

    // Mark initialised and flush any events that arrived during the
    // deferred-init window (e.g. early Page Ready or Web Vital events).
    mixpanelIsInitialised = true;
    flushPendingMixpanelEvents();

    return true;

  } catch (e) {
    console.error('Mixpanel initialization failed:', e);
    return false;
  }
};

// Helper function to check if Mixpanel is available
const isMixpanelInstalled = () => {
  return mixpanel && typeof mixpanel.track === 'function';
};

// Helper function to check if Mixpanel is configured
const isMixpanelConfigured = () => {
  if (!isMixpanelInstalled()) {
    return false;
  }

  if (!getEnvMixpanelToken() || getEnvMixpanelToken().trim() === '') {
    return false;
  }

  return true;
};

// Enhanced tracking with error handling and validation
export const track = (eventName, properties = {}) => {
  if (!eventName) {
    return;
  }

  // Early-captured metadata — do this outside the SDK path so buffered
  // events still carry accurate URL / referrer / timestamp even when
  // they fire before init completes.
  const enrichedProperties = {
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    screen_height: typeof window !== 'undefined' ? window.screen.height : undefined,
    screen_width: typeof window !== 'undefined' ? window.screen.width : undefined,
    ...properties
  };

  // If Mixpanel is not yet initialised (deferredInit window) and a token
  // exists, buffer the event. When initMixpanel runs it will flush the
  // buffer in order.
  if (!mixpanelIsInitialised) {
    if (!getEnvMixpanelToken() || getEnvMixpanelToken().trim() === '') {
      return; // No token configured — drop silently.
    }
    if (pendingMixpanelEvents.length < MIXPANEL_PENDING_CAP) {
      pendingMixpanelEvents.push({ eventName, properties: enrichedProperties });
    }
    return;
  }

  if (!isMixpanelConfigured()) {
    return;
  }

  try {
    mixpanel.track(eventName, enrichedProperties);
  } catch (e) {
    console.error(`Failed to track event ${eventName}:`, e);
  }
};

// Enhanced user identification with error handling
export const identify = (userId, userProperties = {}) => {
  if (!isMixpanelConfigured() || !userId) {
    console.debug('Mixpanel is not available or user ID is missing');
    return;
  }

  try {
    const previousId = mixpanel.get_distinct_id();

    // First reset the identity to clear any existing state
    mixpanel.reset();

    // Set the distinct_id immediately
    mixpanel.register({ distinct_id: userId });

    // Identify the user
    mixpanel.identify(userId);

    // If this was a signup/first login (previous ID was anonymous), create an alias
    if (previousId && previousId !== userId && previousId.includes('$device')) {
      mixpanel.alias(userId, previousId);
    }

    // Set initial user properties
    mixpanel.people.set_once({
      $distinct_id: userId,
      user_id: userId,
      first_seen: new Date().toISOString()
    });

    // Set/update user properties
    const commonProperties = {
      $last_seen: new Date().toISOString(),
      $browser: navigator.userAgent,
      $browser_version: navigator.appVersion,
      $os: navigator.platform,
      $screen_height: window.screen.height,
      $screen_width: window.screen.width,
      ...userProperties,
      distinct_id: userId
    };

    setUserProperties(commonProperties);

    // Track identification
    track('User Identified', {
      distinct_id: userId,
      user_id: userId,
      is_first_login: previousId && previousId !== userId && previousId.includes('$device')
    });

  } catch (e) {
    console.error('Failed to identify user:', e);
  }
};

// Enhanced user properties setting with validation
export const setUserProperties = (properties = {}) => {
  if (!isMixpanelConfigured() || !properties || Object.keys(properties).length === 0) {
    return;
  }

  try {
    // Filter out any null or undefined values
    const validProperties = Object.entries(properties).reduce((acc, [key, value]) => {
      if (value != null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (Object.keys(validProperties).length > 0) {
      mixpanel.people.set(validProperties);
    }
  } catch (e) {
    console.error('Failed to set user properties:', e);
  }
};

// Increment a numeric property
export const incrementProperty = (property, value = 1) => {
  if (!isMixpanelConfigured() || !property) {
    return;
  }

  try {
    mixpanel.people.increment(property, value);
  } catch (e) {
    console.error(`Failed to increment property ${property}:`, e);
  }
};

// Reset user data (useful for logout)
export const reset = () => {
  if (!isMixpanelConfigured()) {
    return;
  }

  try {
    // Track logout before resetting
    track('User Logged Out');

    // Reset the Mixpanel state
    mixpanel.reset();
  } catch (e) {
    console.error('Failed to reset Mixpanel:', e);
  }
};